import {
  toWSDateString,
  WSCard,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSList,
  WSLoader,
  WSPill,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import { useHistory } from "react-router-dom";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { PATH_PAYABLES } from "../../Payables/paths";
import { useCurrentPayPeriod } from "../hooks/useCurrentPayPeriod";
import { routeHelpersWorkLogsPayerToApprove } from "../paths";
import { WorkLogsDashboardEmptyState } from "./WorkLogsEmptyStates";
import { WidgetWorkLogsPayerView } from "./WidgetWorkLogsPayerView";
import { useSession } from "@hooks/useSession";

export const WorklogsPayerDashboard: React.FC = () => {
  const history = useHistory();
  const { accountId } = useSession();

  // Query to check if any work logs exist
  // Payer scope filtered by `payerAccountId.eq` using the v3 session accountId.
  const queryHasAnyWorkLogs = useQueryWorkLogsSummary({
    filter: { payerAccountId: { eq: accountId } }
  });

  const hasAnyWorkLogs = (queryHasAnyWorkLogs.data?.listSize ?? 0) > 0;

  // Query for "To approve" count
  const queryToApproveCount = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.IN_REVIEW] }
    }
  });

  const toApproveCount = queryToApproveCount.data?.listSize ?? 0;

  // Get current and next pay period info from the v3 pay periods endpoint
  const { currentPayPeriod, nextPayPeriod } = useCurrentPayPeriod();

  // v1 read the payroll schedule's `cutOffDate` here. v3 pay periods carry no
  // pay/cut-off date, so this shows when the next cycle closes instead — the
  // closest date the v3 data on this screen actually supports.
  // TODO(v3-blocker): the real pay date lives on
  // `previewPayrollSchedule` (`periods[].payDate`), which currently answers 501
  // and is shaped as a POST, so the registry exposes it as a mutation rather
  // than a read. Switch to it once the endpoint lands.
  const nextPayrollDate = nextPayPeriod
    ? toWSDateString(nextPayPeriod.endDate, "monthDate")
    : "--";

  // The "Current pay period" card is about *this* period, so its count is scoped
  // by `payPeriodId` — the top card's count is deliberately account-wide.
  const queryCurrentPeriodToApproveCount = useQueryWorkLogsSummary(
    {
      filter: {
        payerAccountId: { eq: accountId },
        payPeriodId: { eq: currentPayPeriod?.id as string },
        status: { anyOf: [WorkLogStatus.IN_REVIEW] }
      }
    },
    { enabled: !!currentPayPeriod?.id }
  );

  const currentPeriodToApproveCount =
    queryCurrentPeriodToApproveCount.data?.listSize ?? 0;

  const currentPayPeriodText = currentPayPeriod
    ? `${toWSDateString(currentPayPeriod.startDate, "monthDayYear")} - ${toWSDateString(currentPayPeriod.endDate, "monthDayYear")}`
    : undefined;

  // Show spinner while loading
  if (queryHasAnyWorkLogs.isLoading) {
    return <WSLoader.Spinner my="2XL" size="S" color="gray500" />;
  }

  // If no work logs exist, show empty state
  if (!hasAnyWorkLogs) {
    return <WorkLogsDashboardEmptyState />;
  }

  return (
    <WSGrid gutter="2XL" mt="XL">
      <WSGrid.Item span={{ m: "8" }}>
        <WSList gap="XL">
          {/* Top-level "To approve" card */}
          <WSCard
            divider="bottom"
            header={{
              label: {
                avatar: {
                  type: "icon",
                  icon: "alert",
                  size: "M",
                  colorBackground: "amber50",
                  color: "amber400"
                },
                text: "To approve:"
              }
            }}
            actions={{
              alignment: "left",
              buttons: [
                {
                  kind: "Link",
                  label: "Review and approve",
                  rightIcon: "chevron-right",
                  size: "S",
                  onClick: () =>
                    history.push(routeHelpersWorkLogsPayerToApprove.path)
                }
              ]
            }}
          >
            <WSText.ParagraphSm color="gray500">
              {toApproveCount} work logs awaiting your review
            </WSText.ParagraphSm>
          </WSCard>

          {/* Next pay period section */}
          <WSElement>
            <WSFlexBox justify="space-between" alignItems="center" mb="M">
              <WSText.Heading5>Next pay period</WSText.Heading5>
              {nextPayPeriod && (
                <WSPill
                  text={`${nextPayPeriod.daysLeftToOpen} days left to open`}
                  theme="blue"
                  icon="info-circle"
                />
              )}
            </WSFlexBox>

            <WSCard
              divider="bottom"
              header={{
                label: {
                  avatar: {
                    type: "icon",
                    icon: "calendar",
                    size: "M",
                    colorBackground: "blue50",
                    color: "blue500"
                  },
                  text: `Next payroll: ${nextPayrollDate}`
                }
              }}
              actions={{
                alignment: "left",
                buttons: [
                  {
                    kind: "Link",
                    label: "View in payables",
                    rightIcon: "chevron-right",
                    size: "S",
                    onClick: () => history.push(PATH_PAYABLES)
                  }
                ]
              }}
            >
              <WSText.ParagraphSm color="gray500">
                This is your next scheduled payment cycle
              </WSText.ParagraphSm>
            </WSCard>
          </WSElement>

          {/* Current pay period section */}
          <WSElement>
            <WSFlexBox justify="space-between" alignItems="center" mb="M">
              <WSText.Heading5>Current pay period</WSText.Heading5>
              {currentPayPeriod && (
                <WSPill
                  text={`${currentPayPeriod.daysLeftToReview} days left to review`}
                  theme="warning"
                  icon="alert"
                />
              )}
            </WSFlexBox>

            <WSCard
              divider="bottom"
              header={{
                label: {
                  avatar: {
                    type: "icon",
                    icon: "alert",
                    size: "M",
                    colorBackground: "amber50",
                    color: "amber400"
                  },
                  text: "To approve:"
                }
              }}
              actions={{
                alignment: "left",
                buttons: [
                  {
                    kind: "Link",
                    label: "Review and approve",
                    rightIcon: "chevron-right",
                    size: "S",
                    // Carries the period through, so the list opens showing the
                    // same work logs this card counts.
                    onClick: () =>
                      history.push(
                        routeHelpersWorkLogsPayerToApprove.createLocationObject(
                          {
                            search: {
                              filter: { payPeriodId: currentPayPeriod?.id }
                            }
                          }
                        )
                      )
                  }
                ]
              }}
            >
              <WSText.ParagraphSm color="gray500">
                {currentPeriodToApproveCount} work logs in this pay period
                awaiting your review
              </WSText.ParagraphSm>
            </WSCard>
          </WSElement>
        </WSList>
      </WSGrid.Item>

      <WSGrid.Item span={{ m: "4" }}>
        <WSList gap="XL">
          {/* Current pay period card */}
          {currentPayPeriodText && (
            <WSElement>
              <WSSectionToolbar title="Current pay period" />
              <WSCard>
                <WSText.ParagraphXs color="gray500">
                  Pay period
                </WSText.ParagraphXs>
                <WSText.ParagraphSm color="gray700" mt="XS">
                  {currentPayPeriodText}
                </WSText.ParagraphSm>
              </WSCard>
            </WSElement>
          )}

          <WidgetWorkLogsPayerView
            title="Recent work logs"
            showEngagementDetails
            hideViewAll
          />
        </WSList>
      </WSGrid.Item>
    </WSGrid>
  );
};
